<template>
  <div class="timesheet">
    <div class="layout-header">
      <div class="layout-title">
        <b>Табель</b> {{ timesheetName }}
        <div style="font-size: 15px; margin-top: 5px">Все смены</div>
      </div>
    </div>

    <div class="timesheet__content">
      <div v-if="inited" class="timesheet-chart">
        <Loader v-if="loading" :active="loading" label="Производится расчет" :height="434"></Loader>
        <template v-if="!loading">
          <div class="timesheet-chart__table">
            <table class="h-table">
              <thead>
                <tr>
                  <th
                    v-for="column in columns"
                    :key="column.code"
                    class="timesheet-chart-column"
                    :style="{
                      'width': column.code === 'days' ? days.length * 23 + 'px' : false,
                      'max-width': column.code === 'days' ? days.length * 23 + 'px' : false,
                    }"
                    :class="'timesheet-chart-column__' + column.code"
                  >
                    <div class="h-table__header">
                      <template v-if="column.code === 'days'">
                        <div class="h-table__col-title">
                          {{ monthName }}
                        </div>

                        <div class="timeline">
                          <div
                            v-for="day in chartData.total.days"
                            :key="day.key"
                            class="day"
                            :class="{
                              conflict: day.hasConflict,
                            }"
                          >
                            {{ day.day }}
                          </div>
                        </div>
                      </template>

                      <template v-else>
                        <div class="h-table__col-title">
                          <div class="title">
                            {{
                              [
                                column.ultraShortName,
                                column.unit === "ruble" && "฿",
                                column.unit === "ruble/unit" && "฿/шт.",
                                column.unit === "HH:MM" && "чч:мм",
                              ]
                                .filter(Boolean)
                                .join(", ")
                            }}
                          </div>
                        </div>
                      </template>
                    </div>
                  </th>
                </tr>
              </thead>
              <template v-for="position in positionsList">
                <tr :key="position.code" class="position-name">
                  <template v-for="column in columns">
                    <td
                      v-if="column.code !== 'days' && column.code !== 'loadType'"
                      :key="column.code"
                      :class="'position-name__' + column.code"
                      :colspan="column.code === 'worker' ? (visibleColumns.includes('loadType') ? 3 : 2) : 1"
                    >
                      <table-position-cell
                        :column="column.code"
                        :position="position"
                        :data="chartData.positions[position.code] || []"
                      ></table-position-cell>
                    </td>
                  </template>
                </tr>

                <tbody :key="position.code + '-workers'" class="position-workers">
                  <template v-for="worker in (chartData.positions[position.code] || []).workers">
                    <tr v-if="shiftDayOrNight(worker)" :key="worker.id" class="worker-data">
                      <td v-for="column in columns" :key="column.code" :class="'worker-data__' + column.code">
                        <table-cell
                          :column="column.code"
                          :worker="worker"
                          :position="position"
                          :filial="currentTimesheet && currentTimesheet.filial.id"
                        ></table-cell>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </template>
              <tbody class="tfoot">
                <tr>
                  <template v-for="column in columns">
                    <td
                      v-if="column.code !== 'loadType'"
                      :key="column.code"
                      :colspan="column.code === 'worker' && visibleColumns.includes('loadType') ? 2 : 1"
                      class="timesheet-total-column"
                      :class="'timesheet-total-column__' + column.code"
                    >
                      <table-total-cell
                        :column="column.code"
                        :workers="workersList"
                        :stats="chartData.total"
                      ></table-total-cell>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import Loader from "@/components/loader";
import TableCell from "./components/cell";
import TablePositionCell from "./components/position-cell";
import TableTotalCell from "./components/total-cell";

import { MONTHS } from "@/utils/date/months";
import { Column } from "@/entities/structures/column";
import { Shift, Day, CalculatedDay } from "../entities/shift-models";

const defaultPeriod = moment().startOf("month").format("YYYY-MM-DD");

export default {
  components: {
    Loader,

    TableCell,
    TablePositionCell,
    TableTotalCell,
  },

  data() {
    return {
      id: null,
      currentTimesheet: null,

      inited: false,
      loading: false,

      columnsList: [],
      visibleColumns: [],

      filters: {
        period: defaultPeriod,
        filial: null,
      },

      savedPeriod: defaultPeriod,
      savedFilial: "",

      positions: [],
      visiblePositions: [],

      workersByPositions: [], // данные сотрудников
      shift: "all",

      savedTimesheet: [],
      timesheet: {}, // данные по сменам

      calculations: {},

      timesheetCreatedError: "",
    };
  },

  computed: {
    calculationsByDates() {
      const result = {};
      Object.values(this.calculations).forEach((data) => {
        const days = data.days || [];
        days.forEach((day) => {
          const key = moment(day.date, "YYYY-MM-DD").format("YYYY-MM-DD");
          if (!(key in result)) result[key] = 0;
          result[key] += 1;
        });
      });
      return result;
    },
    defaultColumns() {
      return this.columnsList.map((c) => c.code);
    },
    columns() {
      return this.columnsList.filter((c) => this.visibleColumns.includes(c.code));
    },
    monthName() {
      if (!this.savedPeriod) return "";
      const date = moment(this.savedPeriod, "YYYY-MM-DD");
      if (date.isValid) {
        const month = date.get("month");
        return MONTHS[month];
      }
      return "";
    },

    workersList() {
      let positions = [];
      Object.values(this.workersByPositions).forEach((post) => {
        positions = [...positions, ...post];
      });
      return positions;
    },

    positionsList() {
      return this.visiblePositions.map((code) => this.positions.find((p) => p.code === code));
    },

    days() {
      const start = moment(this.savedPeriod, "YYYY-MM-DD");
      const end = start.clone().endOf("month");

      let current = start.clone();
      const days = [];

      while (current.isSameOrBefore(end)) {
        days.push({
          date: current,
          day: current.get("date"),
          key: current.format("YYYY-MM-DD"),
        });
        current = current.clone().add(1, "d");
      }

      return days;
    },

    timesheetName() {
      if (!this.currentTimesheet) return "";
      const period = moment(this.currentTimesheet.period);

      const month = MONTHS[period.get("month")].toLowerCase();
      const year = period.get("year");
      return `${this.currentTimesheet.filial.filial_name}, ${month} ${year}`;
    },

    chartData() {
      const positionsData = {};

      Object.entries(this.workersByPositions).forEach(([postCode, postData]) => {
        // расчетные данные для должности
        const postCalculations = this.calculations[postCode] ? this.calculations[postCode].days : [];

        const postChartData = postData.map((workerData) => {
          // Данные по сотруднику

          // зарплата сотрудника
          let postSalary = workerData.salaries.find((s) => s.code === postCode);
          postSalary = postSalary ? postSalary.salary : 0;

          let dayShiftsCount = 0; // кол-во дневных смен
          let nightShiftsCount = 0; // кол-во ночных смен
          let totalTimeMin = 0; // рабочее время, мин

          // показатели для реальных смен (из расчета)
          let calcDayShiftsCount = 0;
          let calcNightShiftsCount = 0;
          let calcTotalTimeMin = 0;

          // *отпуска сотрудника
          const workerVacations = workerData.vacations.map((v) => ({
            ...v,
            fromDate: moment(v.from_dt, "YYYY-MM-DD"),
            toDate: moment(v.to_dt, "YYYY-MM-DD"),
          }));

          // *смены сотрудника
          const workerTimesheet = (this.timesheet[workerData.id] && this.timesheet[workerData.id][postCode]) || [];

          // данные по дням
          const workerDays = this.days.map((day) => {
            const dayTimesheet = workerTimesheet.find((d) => d.date === day.key);

            const vacations = workerVacations.filter(
              (v) => day.date.isSameOrAfter(v.fromDate) && day.date.isSameOrBefore(v.toDate),
            );

            // Расчеты для сотрудника на текущий день
            const dayCalculations = postCalculations.filter((d) => {
              if (d.worker_id !== workerData.id) return false;
              const key = moment(d.date, "YYYY-MM-DD").format("YYYY-MM-DD");
              return key === day.key;
            });

            // let dayCalculations = (workerData.calculatedDays || []).filter(
            //   d => {
            //     let key = moment(d.date, "YYYY-MM-DD").format("YYYY-MM-DD");
            //     return key === day.key;
            //   }
            // );

            // есть ли вообще расчет на текущий день
            const hasDayCalculation = this.calculationsByDates[day.key] > 0;

            const calculatedTimesheet = new CalculatedDay(day.key, dayCalculations);

            const shiftsAndVacationsConflicts =
              ((dayTimesheet && dayTimesheet.shifts.length) ||
                (calculatedTimesheet && calculatedTimesheet.shifts.length)) &&
              vacations.length;

            const planAndFactConflicts = (() => {
              // нет плана на день для сотрудника
              if (!dayTimesheet || !dayTimesheet.shifts.length) {
                // нет расчета для сотрудника
                if (!calculatedTimesheet.shifts.length) {
                  return false; // нет конфликта
                }
                // есть расчет для сотрудника

                return true; // есть конфликт
              }

              // есть план на день для сотрудника

              // нет расчета на текущий день
              if (!hasDayCalculation) {
                return false; // нет конфликта
              }
              // нет расчета для сотрудника
              if (!calculatedTimesheet.shifts.length) {
                return true; // есть конфликт
              }
              // есть расчет для сотрудника

              // расчет совпадает с планом

              if (dayTimesheet.isSame(calculatedTimesheet)) {
                return false; // нет конфликта
              }
              // расчет не совпадает с планом
              return true; // есть конфликт
            })();

            return {
              ...day,
              timesheet: dayTimesheet, // смены в этот день
              calculatedTimesheet, // фактические смены в этот день (расчет)
              visibleTimesheet: hasDayCalculation ? calculatedTimesheet : dayTimesheet,
              totalTimeMin: dayTimesheet ? dayTimesheet.shiftsDuration : 0,
              calcTotalTimeMin: calculatedTimesheet.shiftsDuration,
              vacations, // отпуска в этот день
              hasDayCalculation,
              shiftsAndVacationsConflicts,
              planAndFactConflicts,
              hasConflict: shiftsAndVacationsConflicts || planAndFactConflicts,
            };
          });

          // *подсчет смен и рабочих часов
          workerDays.forEach((day) => {
            if (day.timesheet) {
              dayShiftsCount += day.timesheet.hasDayShift ? 1 : 0;
              nightShiftsCount += day.timesheet.hasNightShift ? 1 : 0;
              totalTimeMin += day.timesheet.shiftsDuration;
            }

            if (day.calculatedTimesheet) {
              calcDayShiftsCount += day.calculatedTimesheet.hasDayShift ? 1 : 0;
              calcNightShiftsCount += day.calculatedTimesheet.hasNightShift ? 1 : 0;
              calcTotalTimeMin += day.calculatedTimesheet.shiftsDuration;
            }
          });

          // Основная часть з/п
          const salaryMain = (postSalary / 12) * (Math.floor((totalTimeMin / 60) * 10) / 10);

          // показывать для дневной смены
          const showForDay = workerData.work_shift_type === "day" || dayShiftsCount > 0 || calcDayShiftsCount > 0;

          // показывать для ночной смены
          const showForNight =
            workerData.work_shift_type === "night" || nightShiftsCount > 0 || calcNightShiftsCount > 0;

          return {
            id: workerData.id,
            name: workerData.name,
            jobStatus: workerData.jobStatus || {},
            filial: workerData.filial,
            salary: postSalary, // оклад на данной должности
            shiftType: workerData.work_shift_type, // тип смены (дневная/ночная)
            schedule: workerData.schedule, // график работы
            days: workerDays, // данные по дням
            dayShiftsCount, // кол-во дневных смен
            nightShiftsCount, // кол-во ночных смен
            totalTimeMin, // время работы, мин
            calcDayShiftsCount, // кол-во дневных смен
            calcNightShiftsCount, // кол-во ночных смен
            calcTotalTimeMin, // время работы, мин
            salaryMain, // основная часть з/п
            calcSalaryMain: workerData.salaryMainPart,
            vacations: workerVacations, // Отпуска
            timesheet: workerTimesheet, // смены
            showForDay, // показывать для дневной смены
            showForNight, // показывать для ночной смены

            // фактические данные из расчета
            compensation: workerData.compensation || 0,
            depreciation: workerData.depreciation || 0,
            excessPremium: workerData.excessPremium || 0,
            fine: workerData.fine || 0,
            load: workerData.load || 0,
            premium: workerData.premium || 0,
            totalAccrued: workerData.totalAccrued || 0,
            penalties: workerData.penalties || 0,
          };
        });

        // данные по должности

        const reducedPostData = {
          salary: 0, // оклад для должности
          salaryMain: 0, // основная часть зп для должности
          totalTimeMin: 0, // рабочее время для должности, мин
          dayShiftsCount: 0, // Дневных смен для должности
          nightShiftsCount: 0, // ночных смен для должности

          calcSalaryMain: 0,
          calcTotalTimeMin: 0, // рабочее время для должности, мин
          calcDayShiftsCount: 0, // Дневных смен для должности
          calcNightShiftsCount: 0, // ночных смен для должности

          // фактические данные из расчета
          compensation: 0,
          depreciation: 0,
          excessPremium: 0,
          fine: 0,
          load: 0,
          premium: 0,
          totalAccrued: 0,
          penalties: 0,
        };

        const postDays = this.days.map((d) => ({
          ...d,
          totalTimeMin: 0,
          hasConflict: false,
        }));

        postChartData.forEach((workerChartData) => {
          Object.keys(reducedPostData).forEach((key) => {
            reducedPostData[key] += workerChartData[key];
          });

          workerChartData.days.forEach((day, i) => {
            postDays[i].totalTimeMin += day.hasDayCalculation ? day.calcTotalTimeMin : day.totalTimeMin;
            if (day.hasConflict) postDays[i].hasConflict = true;
          });
        });

        positionsData[postCode] = {
          workers: postChartData, // сотрудники на должности
          days: postDays, // данные по должности по дням
          ...reducedPostData,
        };
      });

      const reducedTotalData = {
        salary: 0, // оклад для должности
        salaryMain: 0, // основная часть зп для должности
        totalTimeMin: 0, // рабочее время для должности, мин
        dayShiftsCount: 0, // Дневных смен для должности
        nightShiftsCount: 0, // ночных смен для должности
        calcSalaryMain: 0,
        calcTotalTimeMin: 0, // рабочее время для должности, мин
        calcDayShiftsCount: 0, // Дневных смен для должности
        calcNightShiftsCount: 0, // ночных смен для должности

        // фактические данные из расчета
        compensation: 0,
        depreciation: 0,
        excessPremium: 0,
        fine: 0,
        load: 0,
        premium: 0,
        totalAccrued: 0,
        penalties: 0,
      };

      const totalDays = this.days.map((d) => ({
        ...d,
        totalTimeMin: 0,
        hasConflict: false,
      }));

      Object.values(positionsData).forEach((positionChartData) => {
        Object.keys(reducedTotalData).forEach((key) => {
          reducedTotalData[key] += positionChartData[key];
        });

        positionChartData.days.forEach((day, i) => {
          totalDays[i].totalTimeMin += day.totalTimeMin;
          if (day.hasConflict) totalDays[i].hasConflict = true;
        });
      });

      const totalData = {
        days: totalDays,
        ...reducedTotalData,
      };

      const result = {
        positions: positionsData,
        total: totalData,
      }; 

      return result;
    },
  },

  watch: {
    inited(v) {
      if (v)
        setTimeout(() => {
          window.print();
        }, 1000);
    },
  },

  created() {
    this.loading = true;
    this.injectCss();

    this.$store.dispatch("timesheets/getPositions").then((items) => {
      this.positions = items;
    });

    Promise.all([
      this.getColumnsSet().then((res) => {
        this.columnsList = res;
      }),
      this.getStartColumnsSet().then((res) => {
        this.visibleColumns = res;
      }),
      this.$store.dispatch("timesheets/getPositions").then((items) => {
        this.positions = items;
      }),
    ])
      .then(() =>
        this.loadData({ id: this.$route.params.id }).then((res) => {
          this.currentTimesheet = res;

          this.filters.period = this.currentTimesheet.period;
          this.filters.filial = {
            value: this.currentTimesheet.filial.id,
            label: this.currentTimesheet.filial.filial_name,
          };

          this.savedPeriod = this.filters.period;

          this.savedTimesheet = res.days;

          this.calculations = res.calculations;

          this.timesheet = this.groupShifts(this.savedTimesheet);

          this.workersByPositions = this.formatPostsData(res.posts);
        }),
      )
      .then(() => {
        const positions = Object.keys(this.workersByPositions);
        this.visiblePositions = this.positions.filter((p) => positions.includes(p.code)).map((p) => p.code);

        this.loading = false;
        this.inited = true;
      });
  },

  methods: {
    shiftDayOrNight(worker) {
      return (
        this.shift === "all" ||
        (this.shift === "day" && worker.showForDay) ||
        (this.shift === "night" && worker.showForNight)
      );
    },
    getColumnsSet() {
      return this.$store.dispatch("timesheets/getColumnsList", { section: "timesheet" }).then((columns) => {
        columns.sort((a, b) => a.sort - b.sort);
        return columns.map((c) => new Column(c));
      });
    },
    getStartColumnsSet() {
      return this.$store.dispatch("timesheets/getUserColumnsList", { section: "timesheet" }).then((res) => res.columns);
    },
    injectCss() {
      const css = `@page { size: A4 landscape;
      margin: 10mm 10mm 10mm 10mm; }`;
      const head = document.head || document.getElementsByTagName("head")[0];
      const style = document.createElement("style");

      style.type = "text/css";
      style.media = "print";

      if (style.styleSheet) {
        style.styleSheet.cssText = css;
      } else {
        style.appendChild(document.createTextNode(css));
      }

      head.appendChild(style);
    },
    loadData(params) {
      return Promise.all([
        this.$store.dispatch("timesheets/getData", params),
        this.$store.dispatch("timesheets/getCalculation", params),
      ]).then(([data, calculations]) => ({
        ...data,
        calculations,
      }));
    },

    groupShifts(days) {
      const workers = {};

      days.forEach((day) => {
        if (!(day.worker_id in workers)) {
          workers[day.worker_id] = {};
        }

        const worker = workers[day.worker_id];

        if (!(day.post_code in worker)) {
          worker[day.post_code] = [];
        }

        const positionShifts = worker[day.post_code];

        positionShifts.push(
          new Day({
            date: day.date,
            shifts: day.shifts.map(
              (s) =>
                new Shift({
                  type: s.shift_type,
                  fromTime: s.from_time,
                  toTime: s.to_time,
                }),
            ),
          }),
        );
      });

      return workers;
    },

    formatPostsData(posts) {
      const result = {};

      // Сотрудники, сохраненные в табеле
      Object.entries(posts).forEach(([postCode, postData]) => {
        const savedWorkers = postData.workers;
        result[postCode] = savedWorkers;
      });

      // Добавление сотрудников из ежедневных расчетов
      Object.entries(this.calculations).forEach(([postCode, postData]) => {
        const calculatedPostWorkers = {};

        postData.days.forEach((day) => {
          const { worker } = day;
          calculatedPostWorkers[worker.id] = worker;
        });

        const calculatedPostWorkersList = Object.values(calculatedPostWorkers);

        if (calculatedPostWorkersList.length) {
          // В табеле нет должности
          if (!result[postCode]) {
            result[postCode] = calculatedPostWorkersList;
          } else {
            // Добавить только новых сотрудников
            const savedPostWorkers = result[postCode];
            const savedPostWorkersIds = savedPostWorkers.map((w) => w.id);

            const newWorkers = calculatedPostWorkersList.filter((w) => !savedPostWorkersIds.includes(w.id));

            result[postCode] = [...savedPostWorkers, ...newWorkers];
          }
        }
      });

      Object.values(result).forEach((workersList) => {
        workersList.sort((a, b) => (a.name > b.name ? 1 : -1));
      });

      return result;
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/assets/styles/vars";

.layout {
  &-header {
    height: auto;
  }
}

.h-table th,
.h-table td {
  padding: 8px 4px;
}
.timesheet {
  width: 2000px;
  padding: 35px;
  .timesheet-total-column__days {
    padding: 0;
    max-width: none;
    height: 1px;
    .cell {
      height: 100%;
    }
  }

  .timesheet-total-column__worker {
    z-index: 2;
  }
  .layout-title {
    font-weight: 400;
    b {
      font-weight: 500;
    }
  }

  &-chart {
    margin-top: 30px;

    .h-table {
      min-width: 0;
    }

    &-column {
      width: 50px;
      min-width: 50px;
      max-width: 50px;

      &__days {
        padding-left: 0 !important;
        padding-right: 0 !important;
        max-width: none;

        .h-table__col-title {
          padding-left: 8px;
          padding-right: 8px;
        }
      }

      &__compensation,
      &__loadType {
        width: 60px;
        min-width: 60px;
        max-width: 60px;
      }

      &__load {
        width: 65px;
        min-width: 65px;
        max-width: 65px;
      }
    }
    &__actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-left: -5px;
      margin-right: -5px;
    }
    &__actions-group {
      display: flex;
      align-items: center;
    }
    &__action {
      margin-left: 5px;
      margin-right: 5px;
      margin-bottom: 20px;
    }

    &__table {
      &.timesheet-chart-column__loadType {
        max-width: 55px;
      }
      thead.fixed {
        z-index: 3;
      }
      .tfoot tr {
        background: #f5f7fa;
      }
      .position-name {
        background: #f5f7fa;

        &__worker {
        }

        &__loadType {
          border-color: transparent;
        }
      }
      .timeline {
        display: flex;
        height: 40px;

        margin-right: -1px;
        margin-bottom: -9px;
      }
      .day {
        width: 24px;
        flex-shrink: 0;
        border: 1px solid #dfe4ef;
        margin-left: -1px;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        &:last-child {
          border-right-color: transparent;
        }

        &:before {
          content: "";
          position: absolute;
          top: -1px;
          left: -2px;
          right: -2px;
          bottom: -1px;
          border: 2px solid transparent;
          z-index: 3;
          border-left-color: transparent;
          border-right-color: transparent;
        }

        &.conflict {
          position: relative;
          &:before {
            border-top-color: #ff238d;
            border-bottom-color: #ff238d;
          }
          &:last-child:before {
            border-right-color: #ff238d;
          }
        }

        &:not(.conflict) + .conflict {
          &:before {
            border-left-color: #ff238d;
          }
        }

        &.conflict + .day:not(.conflict) {
          &:before {
            border-left-color: #ff238d;
            left: -1px;
          }
        }
      }
      .worker-data {
        &__worker {
          width: 90px;
          min-width: 90px;
          max-width: 90px;
          background: white;
          z-index: 2;
        }
        &__loadType {
          width: 55px;
          min-width: 55px;
          max-width: 55px;
        }
        &__days {
          padding: 0;
          height: 1px;
          min-height: 0;
          .cell {
            height: 100%;
          }
        }
      }
      & /deep/ {
        .cell {
          justify-content: flex-end;
          &__days {
            justify-content: flex-start;
          }
          &__worker {
            justify-content: flex-start;
          }
          &__loadType {
            justify-content: flex-start;
          }
          &__totalShifts {
            .shifts {
              div {
                width: 100%;
                position: relative;
                padding-left: 20px;
                svg {
                  position: absolute;
                  top: 0;
                  bottom: 0;
                  left: 0;
                  margin: auto;
                }
              }
            }
          }
        }
      }
    }
  }
  &__actions {
    margin-top: 25px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
  &__reset {
    margin-right: 10px;
  }
  & /deep/ {
    .c-button__dropdown {
      &__container {
        max-width: none;
        &__list__item__label {
          white-space: nowrap;
        }
      }
    }
  }
  & /deep/ .ps__rail-x {
    z-index: 4;
  }
}
</style>
